import React from 'react';
import descriptions from '../../../config/descriptions'

// Function based component, represents a single detail entry, which
// preferably resides inside a detail group component
function Detail(props) {
  const [isHovered, setIsHovered] = React.useState(false);
  const [showExplanation, setShowExplanation] = React.useState(false);
  const [isExtended, setIsExtended] = React.useState(false); // State to track if scroll box is fully extended
  const [expandedProducts, setExpandedProducts] = React.useState({}); // Track which products are expanded
  const explanationRef = React.useRef(null);
  let hoverTimeout;
  let leaveTimeout;

  const getAttributeDescription = () => {
    if (descriptions[props.attribute_name]) {
      return descriptions[props.attribute_name]
    }
    // default text if no description found
    return 'No description available';
  };

  const handleMouseEnter = () => {
    if (!explanationRef.current) {
      hoverTimeout = setTimeout(() => {
        setIsHovered(true);
        explanationRef.current = true;
      }, 1000);
    }
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeout);
    clearTimeout(leaveTimeout);

    leaveTimeout = setTimeout(() => {
      setIsHovered(false);

      // Keep the explanation box open if it was manually toggled
      if (!showExplanation) {
        explanationRef.current = false;
      }
    }, 300);
  };

  const handleToggleExplanation = () => {
    setShowExplanation(!showExplanation);
  };

  const handleExplanationClick = (e) => {
    // Stop the event propagation to prevent hiding the box when clicking inside it
    e.stopPropagation();

    // Toggle the explanation box visibility
    setShowExplanation(!showExplanation);

    // If the explanation box is manually toggled, consider it hovered
    explanationRef.current = !showExplanation;
  };

  const handleValueClick = () => {
    setIsExtended(!isExtended); // Toggle the state to extend or reduce the scroll box
  };

  // Toggle a specific product's indication visibility
  const toggleProductIndication = (productName, e) => {
    e.stopPropagation(); // Prevent parent click handlers
    setExpandedProducts(prev => ({
      ...prev,
      [productName]: !prev[productName]
    }));
  };

  // Initialize expandedProducts when the component mounts or when props.value changes
  React.useEffect(() => {
    if (props.attribute_name === "eu_indication_initial_all" && props.value) {
      // All products closed by default
      setExpandedProducts({});
      
      // Set the detail to extended view if it's EU indication
      if (!isExtended) {
        setIsExtended(true);
      }
    }
  }, [props.value, props.attribute_name]);

  // Cleanup timeouts on unmount
  React.useEffect(() => {
    return () => {
      clearTimeout(hoverTimeout);
      clearTimeout(leaveTimeout);
    };
  }, []);

  const getATCLink = (code) => {
    // Only create the link if the code matches expected ATC code patterns
    return `https://atcddd.fhi.no/atc_ddd_index/?code=${code}`;
  };

  const isATCkey = (key) => {
    const atcKeys = [
      'atc_code',
      'atc_code_nameL1',
      'atc_code_nameL2',
      'atc_code_nameL3',
      'atc_code_nameL4',
      'atc_code_nameL5'
    ];
    return atcKeys.includes(key);
  }

  const cleanATCCODE = (text) => {
    // Trim the input to avoid leading or trailing spaces
    text = text.trim();

    // Case where code is already valid
    if (isValidATCCode(text)) {
      return text;
    }

    // Split the string at the first dash ("-") to separate the ATC code and description
    const parts = text.split('-');

    // If there is no dash or the description part is missing, return false
    if (parts.length < 2 || parts[1].trim() === '') {
      return false;
    }

    // The ATC code (part before the dash)
    const atcCode = parts[0].trim();
    return atcCode
  }

  // Helper function to determine if the ATC code is valid
  const isValidATCCode = (atcCode) => {
    // Regex pattern to match ATC codes like A, A04, A04A, A04AA, A04AA05
    const atcCodePattern = /^[A-Z]([0-9]{2}[A-Z]*[0-9]*)?$/i;
    return atcCodePattern.test(atcCode);
  };

  // Parse products and their indications from the value string
  const parseProductsFromValue = (valueStr) => {
    if (!valueStr || typeof valueStr !== 'string') return [];

    // Extract total products count
    const totalProductsMatch = valueStr.match(/Total presentations at marketing authorisation: (\d+)/);
    const totalProducts = totalProductsMatch ? parseInt(totalProductsMatch[1]) : 0;

    // Split by product delimiter
    const productSections = valueStr.split('----------------------------------------');
    
    const products = [];
    
    // Skip the first section if it contains the total products information
    let startIndex = 0;
    if (productSections[0].includes('Total presentations at marketing authorisation:')) {
      startIndex = 1;
    }
    
    for (let i = startIndex; i < productSections.length; i++) {
      const section = productSections[i].trim();
      if (!section) continue;
      
      // Extract product name and indication
      const productMatch = section.match(/Presentation: (.*?)(?=Indication:|$)/s);
      const indicationMatch = section.match(/Indication: (.*?)(?=$)/s);
      
      if (productMatch && indicationMatch) {
        products.push({
          name: productMatch[1].trim(),
          indication: indicationMatch[1].trim()
        });
      }
    }
    
    return products;
  };

  // Render special handling for EU indication
  
  const renderEUIndication = () => {
    // Extract the text content from props.value
    let textContent = "";
    
    // Handle both string and React element cases
    if (typeof props.value === 'string') {
      // If props.value is a simple string
      textContent = props.value;
    } else if (props.value && props.value.props && props.value.props.children) {
      // If props.value is a React element with children (like in the new structure)
      const children = props.value.props.children;
      
      // The first child should be the text content (detail.value)
      if (Array.isArray(children) && children.length > 0) {
        textContent = children[0];
      }
    }
  
    const products = parseProductsFromValue(textContent);
    
    if (products.length === 0) {
      return props.value; // Return the original value with link if no products found
    }
  
    // Extract link component from props.value if it exists
    let linkComponent = null;
    if (props.value && props.value.props && props.value.props.children) {
      const children = props.value.props.children;
      if (Array.isArray(children) && children.length > 1) {
        // Assuming the second child is the link
        linkComponent = children[1];
      }
    }
  
    return (
      <div className="eu-indication-container">
        {textContent.includes('Total presentations at marketing authorisation:') && (
          <div className="total-products">
            <span className="total-count-text">{textContent.match(/Total presentations at marketing authorisation: \d+/)[0]}</span>
            {linkComponent && (
              <span className="external-link-container" style={{ fontWeight: 'normal' }}>
                {' '}{linkComponent}
              </span>
            )}
          </div>
        )}
        
        {products.map((product, index) => (
          <div key={index} className="product-indication">
            <div
              className="product-header"
              onClick={(e) => toggleProductIndication(product.name, e)}
            >
              <span className="product-name"><strong>Presentation:</strong> {product.name}</span>
              <span className="toggle-indicator">
                {expandedProducts[product.name] ? '▼' : '►'}
              </span>
            </div>
            
            {expandedProducts[product.name] && (
              <div className="product-indication-text">
                <strong>Indication:</strong> {product.indication}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="med-info-detail">
      <span
        className="med-info-detail-name"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleToggleExplanation}
      >
        {props.name.endsWith("ADMIN ONLY") ? (
          <>
            {props.name.slice(0, -11)} {/* Slice off " ADMIN ONLY" */}
            <span style={{ color: "orange" }}> ADMIN ONLY</span>
          </>
        ) : (
          props.name
        )}{' '}
        <span
          className={`question-mark ${isHovered || showExplanation ? 'show-explanation' : ''}`}
          onClick={handleExplanationClick}
        >
          ?
        </span>
        {(isHovered || showExplanation) && (
          <div
            className={`explanation-box`}
            onClick={(e) => e.stopPropagation()} // Prevent hiding the box when clicking inside it
          >
            <div className="explanation-text">
              {getAttributeDescription()}
            </div>
          </div>
        )}
      </span>
      <span
        className={`med-info-detail-value ${isExtended || props.attribute_name === "eu_indication_initial_all" ? 'extended' : ''}`} // Always extended for EU indication
        onClick={props.attribute_name !== "eu_indication_initial_all" ? handleValueClick : undefined} // No click handler for EU indication
      >
        {props.attribute_name === "eu_indication_initial_all" ? (
          renderEUIndication()
        ) : isATCkey(props.attribute_name) && isValidATCCode(cleanATCCODE(props.value)) ? (
          <>
            <a
              href={getATCLink(cleanATCCODE(props.value.split('-')[0]))}
              target="_blank"
              rel="noopener noreferrer"
              title={`Link to WHO ATC Index for ${props.value.split(' -')[0]}`}
            >
              {props.value.split(' -')[0]}
              <i className="bx bx-link-external" />
            </a>
            {props.value.includes('-') ? ' - ' + props.value.split('-').slice(1).join('-').trim() : ''}
          </>
        ) : (
          props.value
        )}
      </span>
    </div>
  );
}

export default Detail;